import { Icon } from '@buggy/shared'
import { skipToken } from '@reduxjs/toolkit/query'
import { Button, Spin, Tabs } from 'antd'
import { head } from 'lodash-es'
import { FunctionComponent, useCallback, useMemo, useState } from 'react'
import styled from 'styled-components'
import {
  useDeleteCustomizedCollaborationRecordMutation,
  useFetchCampaignCollaborationRecordsInfiniteQuery,
  useFetchCustomizedCollaborationRecordsInfiniteQuery,
  useFetchReportCollaborationRecordsInfiniteQuery,
} from '@/api/workspace-api/directory-api'
import CardErrorCosmos from '@/components/common/card/card-error-cosmos'
import PermissionInsufficientTooltip from '@/components/common/permission-insufficient-tooltip'
import AddCollaborationRecordModal from '@/components/kol/common/kol-cooperation-detail-drawer/cooperation-history/add-collaboration-record-modal'
import CampaignCta from '@/components/kol/common/kol-cooperation-detail-drawer/cooperation-history/campaign-cta'
import CollaborationRecordList, {
  type CollaborationRecordListProps,
} from '@/components/kol/common/kol-cooperation-detail-drawer/cooperation-history/collaboration-record-list'
import EditCollaborationRecordModal from '@/components/kol/common/kol-cooperation-detail-drawer/cooperation-history/edit-collaboration-record-modal'
import ReportCta from '@/components/kol/common/kol-cooperation-detail-drawer/cooperation-history/report-cta'
import useNotification from '@/hooks/antd/use-notification'
import useCampaignVisible from '@/hooks/use-campaign-visible'
import useEffectUntilResolve from '@/hooks/use-effect-until-resolve'
import { useDirectoryActionPermission } from '@/hooks/use-permission'
import { useIntl } from '@/i18n/hooks/use-intl'
import {
  CollaborationRecordScoringType,
  type CollaborationRecord,
  type FetchCollaborationRecordsResponse,
  type FetchCustomPostCollaborationRecordsResponse,
} from '@/types/schema/directory/collaboration-record'
import type { KolSchemaModel } from '@/types/schema/kol-schema'
import {
  ampli,
  type AccessKolCollaborationTabInDrawerProperties,
} from '@/utils/ampli'
import logger from '@/utils/logger'
import { toVoidFunction } from '@/utils/to-void-function'

enum TabKey {
  Campaign = 'campaign',
  Report = 'report',
  Customized = 'customized',
}

interface CooperationHistoryProps {
  kolInfo?: KolSchemaModel
}

const CooperationHistory: FunctionComponent<CooperationHistoryProps> = ({
  kolInfo,
}) => {
  const { formatMessage, formatNumber } = useIntl()
  const notification = useNotification()

  const [addModalOpen, setAddModalOpen] = useState(false)
  const [editingCollaborationRecord, setEditingCollaborationRecord] =
    useState<CollaborationRecord>()
  const kolId = kolInfo?.id

  const campaignVisible = useCampaignVisible()
  const directoryActionPermission = useDirectoryActionPermission()
  const {
    data: fetchCampaignCollaborationRecordsInfiniteData,
    isLoading: isLoadingCampaignCollaborationRecordsInfinite,
    isError: isErrorCampaignCollaborationRecordsInfinite,
    isFetchingNextPage: isFetchingCampaignCollaborationRecordsNextPage,
    hasNextPage: hasCampaignCollaborationRecordsNextPage,
    fetchNextPage: fetchCampaignCollaborationRecordsNextPage,
  } = useFetchCampaignCollaborationRecordsInfiniteQuery(kolId ?? skipToken)
  const {
    data: fetchReportCollaborationRecordsInfiniteData,
    isLoading: isLoadingReportCollaborationRecordsInfinite,
    isError: isErrorForFetchReportCollaborationRecordsInfinite,
    isFetchingNextPage: isFetchingReportCollaborationRecordsNextPage,
    hasNextPage: hasReportCollaborationRecordsNextPage,
    fetchNextPage: fetchReportCollaborationRecordsNextPage,
  } = useFetchReportCollaborationRecordsInfiniteQuery(kolId ?? skipToken)
  const {
    data: fetchCustomizedCollaborationRecordsInfiniteData,
    isLoading: isLoadingCustomizedCollaborationRecordsInfinite,
    isError: isErrorCustomizedCollaborationRecordsInfinite,
    isFetchingNextPage: isFetchingCustomizedCollaborationRecordsNextPage,
    hasNextPage: hasCustomizedCollaborationRecordsNextPage,
    fetchNextPage: fetchCustomizedCollaborationRecordsNextPage,
  } = useFetchCustomizedCollaborationRecordsInfiniteQuery(kolId ?? skipToken)
  const [deleteCustomizedCollaborationRecord] =
    useDeleteCustomizedCollaborationRecordMutation()

  const isLoading =
    isLoadingCampaignCollaborationRecordsInfinite ||
    isLoadingReportCollaborationRecordsInfinite ||
    isLoadingCustomizedCollaborationRecordsInfinite ||
    !kolInfo ||
    !kolId
  const isError =
    isErrorCampaignCollaborationRecordsInfinite ||
    isErrorForFetchReportCollaborationRecordsInfinite ||
    isErrorCustomizedCollaborationRecordsInfinite
  const [campaignCollaborationRecords, totalCampaignCollaborationRecords] =
    useMemo((): [
      FetchCollaborationRecordsResponse['data']['record_list'],
      number,
    ] => {
      return [
        fetchCampaignCollaborationRecordsInfiniteData?.pages.flatMap(
          (page) => page.data.record_list,
        ) ?? [],
        head(fetchCampaignCollaborationRecordsInfiniteData?.pages)?.data
          .total ?? 0,
      ]
    }, [fetchCampaignCollaborationRecordsInfiniteData])
  const [reportCollaborationRecords, totalReportCollaborationRecords] =
    useMemo((): [
      FetchCustomPostCollaborationRecordsResponse['data']['record_list'],
      number,
    ] => {
      return [
        fetchReportCollaborationRecordsInfiniteData?.pages.flatMap(
          (page) => page.data.record_list,
        ) ?? [],
        head(fetchReportCollaborationRecordsInfiniteData?.pages)?.data.total ??
          0,
      ]
    }, [fetchReportCollaborationRecordsInfiniteData])
  const [customizedCollaborationRecords, totalCustomizedCollaborationRecords] =
    useMemo((): [
      FetchCollaborationRecordsResponse['data']['record_list'],
      number,
    ] => {
      return [
        fetchCustomizedCollaborationRecordsInfiniteData?.pages.flatMap(
          (page) => page.data.record_list,
        ) ?? [],
        head(fetchCustomizedCollaborationRecordsInfiniteData?.pages)?.data
          .total ?? 0,
      ]
    }, [fetchCustomizedCollaborationRecordsInfiniteData])

  const sendAccessKolCollaborationTabInDrawerEvent = useCallback(
    (tab: TabKey, isDefault: boolean = false) => {
      if (!kolInfo?.id || !kolInfo?.name) {
        return
      }

      ampli.accessKolCollaborationTabInDrawer({
        kolId: kolInfo?.id,
        kolName: kolInfo?.name,
        tab: ((): AccessKolCollaborationTabInDrawerProperties['tab'] => {
          switch (tab) {
            case TabKey.Campaign:
              return 'Campaign'
            case TabKey.Report:
              return 'Deep Report'
            case TabKey.Customized:
              return 'Custom'
          }
        })(),
        isDefault,
      })
    },
    [kolInfo?.id, kolInfo?.name],
  )

  useEffectUntilResolve(
    (resolve) => {
      if (!kolInfo?.id || !kolInfo?.name) {
        return
      }

      resolve()

      sendAccessKolCollaborationTabInDrawerEvent(
        campaignVisible ? TabKey.Campaign : TabKey.Report,
        true,
      )
    },
    [kolInfo?.id, kolInfo?.name],
  )

  if (isLoading) {
    return (
      <Container>
        <Spin />
      </Container>
    )
  }

  if (isError) {
    return (
      <Container>
        <CardErrorCosmos />
      </Container>
    )
  }

  const handleDelete: CollaborationRecordListProps['onDeleteRecord'] = async (
    record,
  ): Promise<void> => {
    try {
      await deleteCustomizedCollaborationRecord({
        kolId,
        id: record.id,
      }).unwrap()

      notification.success({
        message: formatMessage({
          id: 'general:operation_success',
        }),
        placement: 'bottomRight',
      })
    } catch (error) {
      logger.error(error)
      notification.error({
        message: formatMessage({
          id: 'general:operation_failure',
        }),
        placement: 'bottomRight',
      })
    }
  }

  return (
    <Container>
      <StyledTabs
        items={[
          ...(campaignVisible
            ? [
                {
                  key: TabKey.Campaign,
                  label: formatMessage(
                    {
                      id: 'kol_cooperation_detail_drawer:tabs_campaign_collaboration_record',
                    },
                    {
                      campaignCount: formatNumber(
                        totalCampaignCollaborationRecords,
                      ),
                    },
                  ),
                  children:
                    campaignCollaborationRecords.length > 0 ? (
                      <CollaborationRecordList
                        collaborationRecordScoringType={
                          CollaborationRecordScoringType.CampaignKol
                        }
                        data={campaignCollaborationRecords}
                        hasNextPage={hasCampaignCollaborationRecordsNextPage}
                        isLoadMore={
                          isFetchingCampaignCollaborationRecordsNextPage
                        }
                        kolId={kolId}
                        kolName={kolInfo.name}
                        onLoadMore={toVoidFunction(
                          fetchCampaignCollaborationRecordsNextPage,
                        )}
                      />
                    ) : (
                      <CampaignCta />
                    ),
                },
              ]
            : []),
          {
            key: TabKey.Report,
            label: formatMessage(
              {
                id: 'kol_cooperation_detail_drawer:tabs_report_collaboration_record',
              },
              {
                reportCount: formatNumber(totalReportCollaborationRecords),
              },
            ),
            children:
              reportCollaborationRecords.length > 0 ? (
                <CollaborationRecordList
                  collaborationRecordScoringType={
                    CollaborationRecordScoringType.ReportKol
                  }
                  data={reportCollaborationRecords}
                  hasNextPage={hasReportCollaborationRecordsNextPage}
                  isLoadMore={isFetchingReportCollaborationRecordsNextPage}
                  kolId={kolId}
                  kolName={kolInfo.name}
                  onLoadMore={toVoidFunction(
                    fetchReportCollaborationRecordsNextPage,
                  )}
                />
              ) : (
                <ReportCta />
              ),
          },
          {
            key: TabKey.Customized,
            label: formatMessage(
              {
                id: 'kol_cooperation_detail_drawer:tabs_customized',
              },
              {
                customizedCount: formatNumber(
                  totalCustomizedCollaborationRecords,
                ),
              },
            ),
            children: (
              <CollaborationRecordList
                collaborationRecordScoringType={
                  CollaborationRecordScoringType.Customized
                }
                data={customizedCollaborationRecords}
                hasNextPage={hasCustomizedCollaborationRecordsNextPage}
                isLoadMore={isFetchingCustomizedCollaborationRecordsNextPage}
                kolId={kolId}
                kolName={kolInfo.name}
                onClickEditRecord={setEditingCollaborationRecord}
                onDeleteRecord={handleDelete}
                onLoadMore={toVoidFunction(
                  fetchCustomizedCollaborationRecordsNextPage,
                )}
              />
            ),
          },
        ]}
        tabBarExtraContent={{
          right: (
            <PermissionInsufficientTooltip
              insufficient={!directoryActionPermission}
            >
              <Button
                disabled={!directoryActionPermission}
                icon={<Icon name='plus' />}
                onClick={(): void => setAddModalOpen(true)}
              >
                {formatMessage({
                  id: 'autoreport:chart_field_add_btn',
                })}
              </Button>
            </PermissionInsufficientTooltip>
          ),
        }}
        tabBarGutter={20}
        onChange={(key) => {
          sendAccessKolCollaborationTabInDrawerEvent(key as TabKey)
        }}
      />
      <AddCollaborationRecordModal
        kolInfo={kolInfo}
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
      />
      {editingCollaborationRecord && (
        <EditCollaborationRecordModal
          open
          kolInfo={kolInfo}
          record={editingCollaborationRecord}
          onClose={() => setEditingCollaborationRecord(undefined)}
        />
      )}
    </Container>
  )
}

const Container = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
  padding: 16px 24px 32px 24px;
`

const StyledTabs = styled(Tabs)`
  &&&.ant-tabs {
    flex: 1;

    .ant-tabs-nav {
      border: none;
      margin: 0 0 16px;

      &::before {
        display: none;
      }
    }

    .ant-tabs-nav-wrap {
      padding: 0;

      .ant-tabs-nav-list {
        font-size: 14px;
        font-style: normal;
        font-weight: 400;
        line-height: normal;

        .ant-tabs-tab-active {
          border-radius: 30px;
          border: 1px solid ${({ theme }): string => theme.colors.brand.primary};
        }
      }

      .ant-tabs-ink-bar {
        display: none;
      }
    }
  }
`

export default CooperationHistory
